import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { ListParams } from "app/pages/sales-intelligence/domain/lists/types/list";
import { isCrmList } from "app/pages/sales-intelligence/domain/lists/utils/list-variant";
import type { LeadsSearchResponseDto } from "app/pages/sales-intelligence/modules/leads-search";
import { getMainCompanyDomainOrDefault } from "app/pages/sales-intelligence/modules/leads-search/utils/rows";
import { opportunityListQueryKeyFactory } from "app/pages/sales-intelligence/pages/opportunity-list/query/queryKeyFactory";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

type Variables = { tagId: string; domain: string; text: string; colorHex: string };

export const useSetTagMutation = (listParams: ListParams, domain: string) => {
    const queryClient = useQueryClient();

    const updateRowTagInCache = (tagId: string, tagTitle: string, colorHex: string) => {
        queryClient.setQueriesData<LeadsSearchResponseDto>(
            { queryKey: opportunityListQueryKeyFactory.leadsTableData(listParams.id, "companies") },
            (oldData) => {
                if (!oldData) {
                    return oldData;
                }

                return {
                    ...oldData,
                    rows: oldData.rows.map((row) => {
                        const rowDomain = getMainCompanyDomainOrDefault(row).domain;
                        if (rowDomain === domain) {
                            return { ...row, tag: { id: tagId, title: tagTitle, colorHex } };
                        }

                        return row;
                    }),
                };
            },
        );
    };

    return useMutation({
        mutationFn: ({ tagId, domain }: Variables) => {
            if (isCrmList(listParams)) {
                return salesApiService.listColumns.setTagCRM(listParams.id, tagId, domain);
            }
            return salesApiService.listColumns.setTag(listParams.id, tagId, domain);
        },
        onSuccess: (_data, variables) => {
            updateRowTagInCache(variables.tagId, variables.text, variables.colorHex);
        },
    });
};
